'use es6';

import SeamlessImmutable from 'seamless-immutable';
import { combineReducers } from 'redux';
import { FINDER_FILES_BULK_DELETE, FINDER_FILE_DELETE, FINDER_FILE_RENAME, RAW_ASSET_BUFFER_FETCH, RAW_ASSET_BUFFER_FETCH_BY_PATH, RAW_ASSET_BUFFER_SAVE, RAW_ASSET_BUFFER_UPDATE_SOURCE, RAW_ASSET_PUBLISH, RAW_ASSET_SAVE_BY_PATH, RAW_ASSET_FETCH_HAS_BUFFERED_CHANGES, RAW_ASSET_UPDATE_UPLOAD_PROGRESS, RAW_ASSET_BUFFER_REPLACE, RAW_ASSET_REVERT, RAW_ASSET_BUFFER_SAVE_BY_PATH } from 'DesignData/actions/ActionTypes';
import { RAW_ASSET } from 'DesignData/enums/FolderAssetTypes';
import { PENDING, FAILED, SUCCEEDED } from 'DesignData/enums/RequestStatus';
import RawAsset from 'DesignData/models/RawAsset';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
const EMPTY_RAW_ASSET = new RawAsset(EMPTY_OBJECT); // Raw asset fields to be excluded from state merge
// E.g. after a save/PUT we'd want to keep the `source` in editor
// since the user could have made subsequent changes to it.

const excludedChangeInfoAttrs = ['source'];

function byId(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case RAW_ASSET_REVERT:
    case RAW_ASSET_BUFFER_REPLACE:
    case RAW_ASSET_BUFFER_FETCH_BY_PATH:
    case RAW_ASSET_BUFFER_FETCH:
      {
        const {
          rawAssetId,
          rawAsset
        } = action;

        if (!rawAsset) {
          return state;
        }

        const current = state[rawAssetId];
        const asset = current ? SeamlessImmutable.merge(current, rawAsset, {
          deep: true
        }) : new RawAsset(rawAsset);
        return SeamlessImmutable.set(state, rawAssetId, asset);
      }

    case RAW_ASSET_BUFFER_SAVE:
      {
        const {
          rawAssetId,
          rawAsset
        } = action;

        if (!(rawAsset && rawAssetId)) {
          return state;
        }

        const current = state[rawAssetId] || EMPTY_RAW_ASSET;
        const asset = SeamlessImmutable.without(rawAsset, excludedChangeInfoAttrs);
        const merged = SeamlessImmutable.merge(current, asset, {
          deep: true
        });
        return SeamlessImmutable.set(state, rawAssetId, merged);
      }

    case RAW_ASSET_BUFFER_UPDATE_SOURCE:
      {
        const {
          rawAssetId,
          source
        } = action;

        if (rawAssetId && typeof source === 'string') {
          return SeamlessImmutable.setIn(state, [rawAssetId, 'source'], source);
        }

        return state;
      }

    case FINDER_FILE_RENAME:
      {
        const {
          file,
          fileId
        } = action;
        if (!file || !fileId) return state;
        const rawAsset = new RawAsset(file);
        return SeamlessImmutable.set(state, fileId, rawAsset);
      }

    case FINDER_FILE_DELETE:
      {
        const {
          fileId,
          fileType
        } = action;

        if (!fileId || fileType !== RAW_ASSET) {
          return state;
        }

        return SeamlessImmutable.without(state, fileId);
      }

    case FINDER_FILES_BULK_DELETE:
      {
        const {
          fileIdsByType
        } = action;

        if (!fileIdsByType || (fileIdsByType[RAW_ASSET] || []).length === 0) {
          return state;
        }

        return SeamlessImmutable.without(state, fileIdsByType[RAW_ASSET]);
      }

    default:
      {
        return state;
      }
  }
}

function hasBufferedChangesById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case RAW_ASSET_FETCH_HAS_BUFFERED_CHANGES:
      {
        const {
          rawAssetId,
          hasBufferedChanges
        } = action;
        return hasBufferedChanges === !!hasBufferedChanges ? SeamlessImmutable.set(state, rawAssetId, hasBufferedChanges) : state;
      }

    case RAW_ASSET_PUBLISH:
    case RAW_ASSET_SAVE_BY_PATH:
      {
        const {
          rawAssetId,
          rawAsset
        } = action;
        return rawAsset ? SeamlessImmutable.set(state, rawAssetId, false) : state;
      }

    case RAW_ASSET_BUFFER_REPLACE:
    case RAW_ASSET_BUFFER_SAVE:
    case RAW_ASSET_BUFFER_SAVE_BY_PATH:
      {
        const {
          rawAssetId,
          rawAsset
        } = action;
        return rawAsset ? SeamlessImmutable.set(state, rawAssetId, true) : state;
      }

    default:
      return state;
  }
}

const errorByIdFactory = (...actionType) => {
  const actionTypes = new Set(actionType);
  return (state = EMPTY_OBJECT, action) => {
    if (!actionTypes.has(action.type)) return state;
    const {
      rawAssetId,
      error
    } = action;
    return error ? SeamlessImmutable.set(state, rawAssetId, error) : SeamlessImmutable.without(state, rawAssetId);
  };
};

const fetchErrorById = errorByIdFactory(RAW_ASSET_BUFFER_FETCH, RAW_ASSET_BUFFER_FETCH_BY_PATH);
const saveErrorById = errorByIdFactory(RAW_ASSET_BUFFER_SAVE, RAW_ASSET_BUFFER_SAVE_BY_PATH);
const publishErrorById = errorByIdFactory(RAW_ASSET_PUBLISH);

const statusByIdFactory = (...actionType) => {
  const actionTypes = new Set(actionType);
  return (state = EMPTY_OBJECT, action) => {
    if (!actionTypes.has(action.type)) return state;
    const {
      rawAssetId,
      error,
      rawAsset
    } = action;
    const status = error && FAILED || rawAsset && SUCCEEDED || PENDING;
    return SeamlessImmutable.set(state, rawAssetId, status);
  };
};

const fetchStatusById = statusByIdFactory(RAW_ASSET_BUFFER_FETCH);
const saveStatusById = statusByIdFactory(RAW_ASSET_BUFFER_SAVE, RAW_ASSET_BUFFER_SAVE_BY_PATH);
const publishStatusById = statusByIdFactory(RAW_ASSET_PUBLISH);

function uploadProgressById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case RAW_ASSET_BUFFER_REPLACE:
      {
        const {
          rawAssetId,
          rawAsset,
          error
        } = action;

        if (rawAsset || error) {
          return SeamlessImmutable.without(state, rawAssetId);
        }

        return SeamlessImmutable.set(state, rawAssetId, 0);
      }

    case RAW_ASSET_UPDATE_UPLOAD_PROGRESS:
      {
        const {
          rawAssetId,
          progress
        } = action;
        return SeamlessImmutable.set(state, rawAssetId, progress);
      }

    default:
      {
        return state;
      }
  }
}

function uploadPreviewUrlById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case RAW_ASSET_BUFFER_REPLACE:
      {
        const {
          rawAssetId,
          rawAsset,
          error
        } = action;

        if (rawAsset || error) {
          //  receive || error
          const previewUrl = state[rawAssetId];
          window.URL.revokeObjectURL(previewUrl);
          return SeamlessImmutable.without(state, rawAssetId);
        } else {
          //  request
          const {
            objectUrl
          } = action;
          if (!objectUrl) return state;
          return SeamlessImmutable.set(state, rawAssetId, objectUrl);
        }
      }

    default:
      {
        return state;
      }
  }
}

export default combineReducers({
  byId,
  hasBufferedChangesById,
  fetchErrorById,
  saveErrorById,
  publishErrorById,
  fetchStatusById,
  saveStatusById,
  publishStatusById,
  uploadProgressById,
  uploadPreviewUrlById
});